import { useState } from "react";
import { useDispatch } from "react-redux";
import useMerchantPortalAxios, {
  useMerchantPortalFormData,
} from "../../utils/axios";
import {
  setFlag,
  setIsPopup,
  setLoading,
  setResponseDescription,
  setResponseTitle,
} from "../../features/responseSlice";
import {
  setError as setAppError,
  setFeatureData,
  setTableData,
  setTableLoading,
} from "../store/slices/appSlice";
import { normalizeLastId } from "../utils/loadMore";

function getErrorMessage(error) {
  const errorData = error?.response?.data;

  if (errorData?.errors) {
    const messages = Object.values(errorData.errors).flat();
    if (messages.length > 0) return messages[0];
  }

  if (errorData?.title) return errorData.title;
  if (errorData?.PopupDescription) return errorData.PopupDescription;
  return "Unable to process request. Please try again.";
}

export default function useNewButlerServiceAPI() {
  const client = useMerchantPortalAxios();
  const formClient = useMerchantPortalFormData();
  const dispatch = useDispatch();
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [totalNbOfPage, setTotalNbOfPage] = useState(1);
  const [totalCount, setTotalCount] = useState(1);
  const [butlerConfig, setButlerConfig] = useState(null);

  async function GetButlerTransactions(lastId, dateFrom, dateTo, orderType) {
    const normalizedLastId = normalizeLastId(lastId);
    setOrdersLoading(true);
    dispatch(setTableLoading({ key: "butlerService", loading: true }));
    dispatch(setAppError(null));

    try {
      const response = await client.post("/v1/CashButler/GetButlerTransactions", {
        SessionID: sessionStorage.getItem("SessionID"),
        lastId: normalizedLastId,
        DateFrom: dateFrom,
        DateTo: dateTo,
        OrderType: orderType,
        PageSize: 10
      });

      const nextOrders = response?.data?.Payload?.ButlerTransactions || [];
      setOrders(nextOrders);

      if (nextOrders.length === 0) {
        setTotalNbOfPage(0);
        setTotalCount(0);
      } else {
        setTotalNbOfPage(nextOrders[0]?.TotalPages || 0);
        setTotalCount(nextOrders[0]?.TotalCount || 0);
      }

      dispatch(
        setTableData({
          key: "butlerService",
          data: nextOrders,
          pagination: {
            lastId: normalizedLastId,
            totalPages: nextOrders.length ? nextOrders[0]?.TotalPages || 0 : 0,
            totalCount: nextOrders.length ? nextOrders[0]?.TotalCount || 0 : 0,
            pageSize: 10,
          },
        }),
      );
      dispatch(
        setFeatureData({
          section: "forms",
          key: "butlerService",
          value: {
            dateFrom,
            dateTo,
            orderType,
          },
        }),
      );

      return nextOrders;
    } catch (error) {
      dispatch(
        setTableData({
          key: "butlerService",
          data: [],
          pagination: {
            lastId: normalizedLastId,
            totalPages: 0,
            totalCount: 0,
            pageSize: 10,
          },
        }),
      );
      dispatch(
        setAppError(
          error?.response?.data?.Message ||
            error?.response?.data?.message ||
            getErrorMessage(error),
        ),
      );
      throw error;
    } finally {
      setOrdersLoading(false);
      dispatch(setTableLoading({ key: "butlerService", loading: false }));
    }
  }

  async function CancelButlerDelivery(transactionId) {
    if (!transactionId) return null;

    try {
      const response = await client.post("/v1/CashButler/CancelButlerDelivery", {
        SessionID: sessionStorage.getItem("SessionID"),
        TransactionId: transactionId,
      });

      const payload = response?.data?.Payload || {};
      dispatch(setFlag(1));
      dispatch(setIsPopup(true));
      dispatch(setResponseTitle(payload?.Title || "Butler Cancelled"));
      dispatch(
        setResponseDescription(
          payload?.Message || "Your request has been successfully cancelled.",
        ),
      );

      return payload;
    } catch (error) {
      const payload = error?.response?.data || {};

      if (payload?.PopupTitle === "LoaderError") {
        return null;
      }

      dispatch(setFlag(10));
      dispatch(setLoading("idle"));
      dispatch(setIsPopup(true));
      dispatch(setResponseTitle(payload?.PopupTitle || "Error"));
      dispatch(
        setResponseDescription(
          payload?.PopupDescription || "Could not cancel the order.",
        ),
      );

      throw error;
    }
  }

  async function GetButlerConfig(CurrencyID, Amount, DeliveryService) {
    try {
      const response = await client.post("/v1/CashButler/GetButlerConfig", {
        SessionID: sessionStorage.getItem("SessionID"),
        CurrencyID,
        Amount,
        DeliveryService,
      });

      const payload = response?.data?.Payload || null;
      setButlerConfig(payload);
      dispatch(
        setFeatureData({
          section: "details",
          key: "butlerService",
          value: {
            butlerConfig: payload,
            CurrencyID,
            Amount,
            DeliveryService,
          },
        }),
      );

      return {
        ok: true,
        payload,
      };
    } catch (error) {
      setButlerConfig(null);
      dispatch(
        setFeatureData({
          section: "details",
          key: "butlerService",
          value: {
            butlerConfig: null,
          },
        }),
      );

      return {
        ok: false,
        payload: null,
        message: getErrorMessage(error),
      };
    }
  }

  async function NewButlerRequest(payload) {
    const response = await formClient.post("/v1/CashButler/NewButlerRequest", payload);
    return response?.data?.Payload;
  }

  return {
    GetButlerTransactions,
    CancelButlerDelivery,
    GetButlerConfig,
    NewButlerRequest,
    orders,
    ordersLoading,
    totalNbOfPage,
    totalCount,
    butlerConfig,
  };
}
